/* ============================================================
   Fence Works — photos → a concrete estimate (docs/SERVICE_SWITCHER_PLAN.md
   §13 item 10). Pure, import-free: the unit harness transpiles it alone.

   The service reads the homeowner's photos with a vision model and answers
   in MEASURES, never prices (server/src/concretescan.js cleanScan): per
   surface the kind, the work it suggests, square feet when something gives
   scale, slab sections, the drop in inches at the worst edge, feet of
   cracks, the floor's condition — and overall whether a site visit is
   needed, with the reasons. This file turns that answer into the
   estimator's blocks (like applySlabsToBlocks does for the drawn slabs),
   the assumptions written on the quote, and the words on the card.
   ============================================================ */
export type ScanSurface = { kind: string; jobTypes: string[]; sqft: number | null; sections: number | null; dropIn: number | null; crackFt: number | null; condition: string | null; note: string };
export type Scan = { at: number; surfaces: ScanSurface[]; needsVisit: boolean; visitReasons: string[]; confidence: "low" | "med" | "high"; summary: string; hazards: string[]; photos?: number };

export const SCAN_JOBS = ["lift", "coat", "clean", "seal", "caulk"];
const KIND_LABEL: Record<string, string> = { driveway: "Driveway", apron: "Garage apron", walk: "Sidewalk / walkway", patio: "Patio", "pool-deck": "Pool deck", steps: "Steps / stoop", porch: "Porch", "garage-floor": "Garage floor", basement: "Basement floor", "ac-pad": "A/C pad", other: "Concrete" };
const COND_RANK: Record<string, number> = { good: 0, cracked: 1, "old-coating": 2 };
const num = (x: any): number | null => (x == null || x === "" || !isFinite(Number(x)) ? null : Number(x));
const r1 = (n: number) => Math.round(n * 10) / 10;

export function kindLabel(kind: any): string { return KIND_LABEL[String(kind || "")] || KIND_LABEL.other; }

/* inches of drop → the lift card's drop band (lib/engine.concrete.ts DROPS) */
export function dropIdOf(inches: any): string {
  const n = num(inches);
  if (n == null || n <= 0) return "unsure";
  if (n < 1) return "under-1";
  if (n < 2) return "1-2";
  if (n < 3) return "2-3";
  if (n < 4) return "3-4";
  return "4-plus";
}
/* the worse of two floor conditions */
export function worstCondition(list: any[]): string | null {
  let best: string | null = null;
  (list || []).forEach((c) => { if (c && COND_RANK[c] != null && (best == null || COND_RANK[c] > COND_RANK[best])) best = c; });
  return best;
}

/* the scan's surfaces → the estimator's blocks. Per job type the scan
   suggested: the areas that could be read add up (unknown ones don't), the
   kinds become surfaces, the sections add, the drop is the worst edge, the
   cracks add; a coating takes the worse condition. The first block of that
   type is patched, else one is appended; a blank starter lift the scan didn't
   confirm is dropped. Nothing else is touched. */
export function scanToBlocks(scan: any, blocks: any): any[] {
  const list: any[] = Array.isArray(blocks) ? blocks.map((b) => ({ ...b })) : [];
  const surfaces: ScanSurface[] = scan && Array.isArray(scan.surfaces) ? scan.surfaces : [];
  const by: Record<string, { sqft: number; sqftKnown: boolean; sections: number; surfaces: string[]; dropIn: number | null; crackFt: number; conditions: string[] }> = {};
  surfaces.forEach((s) => {
    if (!s) return;
    const jts = (Array.isArray(s.jobTypes) ? s.jobTypes : []).filter((j) => SCAN_JOBS.includes(j));
    // one measured length of cracks is ONE repair: caulking when the read suggested it, else the
    // lift's or coating's included crack repair — never the same feet priced twice
    const crackTo = jts.includes("caulk") ? "caulk" : jts.includes("lift") ? "lift" : jts.includes("coat") ? "coat" : null;
    jts.forEach((jt) => {
      const g = by[jt] || (by[jt] = { sqft: 0, sqftKnown: false, sections: 0, surfaces: [], dropIn: null, crackFt: 0, conditions: [] });
      const sq = num(s.sqft);
      if (sq != null && sq > 0) { g.sqft += sq; g.sqftKnown = true; }
      const sec = num(s.sections);
      if (sec != null && sec > 0) g.sections += Math.round(sec);
      if (s.kind && !g.surfaces.includes(s.kind)) g.surfaces.push(s.kind);
      const d = num(s.dropIn);
      if (d != null && d > 0 && (g.dropIn == null || d > g.dropIn)) g.dropIn = d;
      const cf = num(s.crackFt);
      if (cf != null && cf > 0 && jt === crackTo) g.crackFt += cf;
      if (s.condition) g.conditions.push(s.condition);
    });
  });
  const found = Object.keys(by);
  if (!found.length) return list;
  // a blank starter lift the scan didn't confirm goes away
  if (!by.lift && list.length === 1 && list[0] && list[0].jobType === "lift" && !(num(list[0].sqft) > 0) && !(num(list[0].sections) > 0) && !(Array.isArray(list[0].surfaces) && list[0].surfaces.length) && (list[0].drop || "unsure") === "unsure") list.splice(0, 1);
  found.forEach((jt) => {
    const g = by[jt];
    const patch: any = { fromScan: true };
    if (jt === "caulk") { if (g.crackFt > 0) patch.ft = Math.round(g.crackFt); }
    else if (g.sqftKnown) patch.sqft = Math.round(g.sqft);
    if (jt === "lift" || jt === "clean" || jt === "seal") patch.surfaces = g.surfaces.filter((k) => k !== "garage-floor" && k !== "basement");
    if (jt === "lift") { if (g.sections > 0) patch.sections = g.sections; patch.drop = dropIdOf(g.dropIn); if (g.crackFt > 0) patch.crackFt = Math.round(g.crackFt); }
    if (jt === "coat") { const c = worstCondition(g.conditions); if (c) patch.condition = c; if (g.crackFt > 0) patch.crackFt = Math.round(g.crackFt); }
    const i = list.findIndex((b) => b && b.jobType === jt);
    if (i >= 0) list[i] = { ...list[i], ...patch };
    else list.push({ jobType: jt, rate: null, ...(jt === "coat" ? { system: "full-flake", condition: "good" } : {}), ...(jt === "lift" ? { drop: "unsure", surfaces: [] } : {}), ...patch });
  });
  return list;
}

/* what the quote should say was read from photos, not measured */
export function scanAssumptions(scan: any): string[] {
  const out: string[] = [];
  const surfaces: ScanSurface[] = scan && Array.isArray(scan.surfaces) ? scan.surfaces : [];
  surfaces.forEach((s) => {
    if (!s) return;
    const bits: string[] = [];
    const sq = num(s.sqft); if (sq != null && sq > 0) bits.push(`~${Math.round(sq)} sq ft`);
    const d = num(s.dropIn); if (d != null && d > 0) bits.push(`~${r1(d)}″ drop at the worst edge`);
    const sec = num(s.sections); if (sec != null && sec > 0) bits.push(`${Math.round(sec)} section${Math.round(sec) === 1 ? "" : "s"}`);
    const cf = num(s.crackFt); if (cf != null && cf > 0) bits.push(`~${Math.round(cf)} ft of cracks`);
    if (s.condition === "cracked") bits.push("cracked or pitted"); else if (s.condition === "old-coating") bits.push("an old coating on it");
    if (bits.length) out.push(`${kindLabel(s.kind)}: ${bits.join(", ")} — read from the photos, measure on site.`);
  });
  if (scan && scan.needsVisit) out.push("Photos only — a site visit is advised" + (Array.isArray(scan.visitReasons) && scan.visitReasons.length ? ": " + scan.visitReasons.slice(0, 3).join("; ") : "") + ".");
  return out;
}

export function scanVisit(scan: any): { needsVisit: boolean; reasons: string[] } {
  if (!scan) return { needsVisit: false, reasons: [] };
  return { needsVisit: !!scan.needsVisit, reasons: Array.isArray(scan.visitReasons) ? scan.visitReasons.filter((r: any) => typeof r === "string" && r).slice(0, 6) : [] };
}

/* the one line on the card */
export function scanSummary(scan: any): string {
  if (!scan || !Array.isArray(scan.surfaces)) return "";
  const jobs: string[] = [];
  scan.surfaces.forEach((s: any) => (Array.isArray(s && s.jobTypes) ? s.jobTypes : []).forEach((j: string) => { if (SCAN_JOBS.includes(j) && !jobs.includes(j)) jobs.push(j); }));
  const n = scan.surfaces.length;
  return [`${n} surface${n === 1 ? "" : "s"}`, jobs.length ? jobs.join(", ") : "no work read", `confidence ${scan.confidence || "low"}`, scan.needsVisit ? "site visit advised" : "photos may be enough"].join(" · ");
}
